import { useEffect, useRef, useState } from "react";
import { getBufferEstatisticas, getBufferTickets, bufferSync, bufferFinalizar } from "../../api/buffer.api";
import { getProgramados } from "../../api/sprint.api";

const REFRESH_SEC = 60;

const BUFFER_ORDER = ["QA", "Producao", "QA Testes", "Entrega"];
const BUFFER_LABEL = { QA: "QA", Producao: "Produção", "QA Testes": "QA Testes", Entrega: "Entrega", Finalizados: "Finalizados" };
const BUFFER_COLOR = { QA: "#a78bfa", Producao: "#fbbf24", "QA Testes": "#4ade80", Entrega: "#38bdf8", Finalizados: "#4ade80" };

function formatDias(d) {
  if (d == null || d === 0) return "—";
  if (d < 1) return `${Math.round(d * 24)}h`;
  return `${Number(d).toFixed(1)} dias`;
}

function alertColor(dias, media) {
  if (!media || !dias) return "#94a3b8";
  if (dias > media * 2) return "#f87171";
  if (dias > media)     return "#fbbf24";
  return "#4ade80";
}

/* ══════════════════════════════════════════════════
   Page
══════════════════════════════════════════════════ */
export default function Gargalos() {
  const [stats,       setStats]       = useState(null);
  const [ticketsData, setTicketsData] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(false);
  const [expanded,    setExpanded]    = useState(null);
  const [countdown,   setCountdown]   = useState(REFRESH_SEC);
  const loadRef = useRef(null);

  async function load() {
    setError(false);
    try {
      /* Sync em bloco separado — erro aqui não impede carregar as estatísticas */
      try {
        const tickets = await getProgramados();
        if (Array.isArray(tickets) && tickets.length) {
          await bufferSync(tickets.map(tk => ({ id_ticket: tk.id, status_atual: tk.status })));
        }
      } catch {
        /* silencioso — backfill não é crítico para exibir a página */
      }

      const [s, t] = await Promise.all([
        getBufferEstatisticas(),
        getBufferTickets(),
      ]);
      setStats(s);
      setTicketsData(Array.isArray(t) ? t : []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  loadRef.current = load;

  const [finalizing, setFinalizing] = useState(null);

  async function handleFinalizar(idTicket) {
    if (!window.confirm(`Mover o ticket #${idTicket} para Finalizados?\n\nIsso apenas encerra o acompanhamento interno de buffer — não altera o status do ticket no Redmine.`)) return;
    setFinalizing(idTicket);
    try {
      await bufferFinalizar(idTicket);
      await load();
    } catch {
      alert("Erro ao finalizar ticket.");
    } finally {
      setFinalizing(null);
    }
  }

  useEffect(() => {
    loadRef.current();
    const id = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { loadRef.current(); return REFRESH_SEC; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const mediaMap = Object.fromEntries(
    (stats?.resumo || []).map(r => [r.buffer, r.media_dias])
  );

  /* ordena tickets por tempo total decrescente */
  const ticketsSorted = [...ticketsData].sort((a, b) => {
    const totalA = BUFFER_ORDER.reduce((s, k) => s + (a.tempos?.[k] || 0), 0);
    const totalB = BUFFER_ORDER.reduce((s, k) => s + (b.tempos?.[k] || 0), 0);
    return totalB - totalA;
  });

  if (loading) return (
    <div style={page}>
      <div style={{ color: "#475569", fontSize: 13, padding: 40, textAlign: "center" }}>Carregando dados de buffer…</div>
    </div>
  );

  if (error) return (
    <div style={page}>
      <div style={{ color: "#f87171", textAlign: "center", marginTop: 60, fontSize: 14 }}>
        Erro ao carregar dados.{" "}
        <button onClick={load} style={{ color: "#60a5fa", background: "none", border: "none", cursor: "pointer", fontSize: 14 }}>
          Tentar novamente
        </button>
      </div>
    </div>
  );

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={header}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h1 style={h1}>Gargalos de Buffer</h1>
          <span style={sprintBadge}>Sprint Atual</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 11, color: "#475569" }}>Atualiza em {countdown}s</span>
          <button
            onClick={() => { loadRef.current(); setCountdown(REFRESH_SEC); }}
            title="Atualizar agora"
            style={btnRefresh}
          >↻</button>
        </div>
      </header>

      {/* ── KPI Cards ── */}
      <section style={kpiRow}>
        {BUFFER_ORDER.map(buf => {
          const r     = (stats?.resumo || []).find(x => x.buffer === buf) || {};
          const color = BUFFER_COLOR[buf];
          return (
            <div key={buf} style={{ ...kpiCard, borderColor: `${color}30`, boxShadow: `0 0 24px ${color}08` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, boxShadow: `0 0 6px ${color}`, flexShrink: 0 }} />
                <span style={{ color, fontWeight: 700, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  {BUFFER_LABEL[buf]}
                </span>
              </div>
              <div style={{ fontSize: 30, fontWeight: 800, color: "#f1f5f9", lineHeight: 1, marginBottom: 4 }}>
                {formatDias(r.media_dias ?? null)}
              </div>
              <div style={{ fontSize: 11, color: "#475569", marginBottom: 14 }}>tempo médio por ciclo</div>
              <div style={kpiRow2}>
                <span style={kpiLabel}>Mediana</span>
                <span style={kpiValue}>{formatDias(r.mediana_dias ?? null)}</span>
              </div>
              <div style={kpiRow2}>
                <span style={kpiLabel}>Máximo registrado</span>
                <span style={kpiValue}>{formatDias(r.max_dias ?? null)}</span>
              </div>
              <div style={kpiRow2}>
                <span style={kpiLabel}>Ciclos completos</span>
                <span style={kpiValue}>{r.total_tickets ?? 0}</span>
              </div>
              <div style={{ ...kpiRow2, marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                <span style={kpiLabel}>Aguardando agora</span>
                <span style={{ fontWeight: 700, color }}>{r.tickets_abertos ?? 0}</span>
              </div>
            </div>
          );
        })}

        {/* Card Finalizados — buffer manual, sem tempo médio (é sempre instantâneo) */}
        {(() => {
          const r     = (stats?.resumo || []).find(x => x.buffer === "Finalizados") || {};
          const color = BUFFER_COLOR.Finalizados;
          return (
            <div style={{ ...kpiCard, borderColor: `${color}30`, boxShadow: `0 0 24px ${color}08` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, boxShadow: `0 0 6px ${color}`, flexShrink: 0 }} />
                <span style={{ color, fontWeight: 700, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  Finalizados
                </span>
              </div>
              <div style={{ fontSize: 30, fontWeight: 800, color: "#f1f5f9", lineHeight: 1, marginBottom: 4 }}>
                {r.total_tickets ?? 0}
              </div>
              <div style={{ fontSize: 11, color: "#475569", marginBottom: 14 }}>tickets movidos manualmente</div>
              <div style={{ fontSize: 11, color: "#334155", lineHeight: 1.5 }}>
                Controle interno — não altera o status do ticket no Redmine.
              </div>
            </div>
          );
        })()}
      </section>

      {/* ── Tickets aguardando agora ── */}
      <section style={section}>
        <h2 style={sectionTitle}>Tickets aguardando agora</h2>
        <p style={sectionDesc}>
          Tickets atualmente parados em algum buffer, ordenados pelo mais antigo primeiro.
          {" "}<span style={{ color: "#fbbf24" }}>~</span> indica que a entrada foi estimada (ticket já estava no buffer quando o rastreamento foi ativado).
        </p>

        {(stats?.alertas || []).length === 0 ? (
          <div style={emptyMsg}>Nenhum ticket aguardando no momento.</div>
        ) : (
          <div style={tableWrap}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>Ticket</th>
                  <th style={th}>Buffer</th>
                  <th style={th}>Tempo atual</th>
                  <th style={th}>Entrada</th>
                  <th style={th}>Status de entrada</th>
                  <th style={th}>Sprint</th>
                  <th style={th} />
                </tr>
              </thead>
              <tbody>
                {[...(stats.alertas)].sort((a, b) => b.dias_no_buffer - a.dias_no_buffer).map(a => {
                  const color  = BUFFER_COLOR[a.buffer] ?? "#94a3b8";
                  const dias   = Number(a.dias_no_buffer);
                  const media  = mediaMap[a.buffer];
                  const ac     = alertColor(dias, media);
                  const critico = media && dias > media * 2;
                  const acima   = media && dias > media && !critico;
                  return (
                    <tr key={`${a.id_ticket}-${a.buffer}-${a.entrada_em}`} style={trRow}>
                      <td style={td}>
                        <a
                          href={`https://redmine.lexsis.com.br/issues/${a.id_ticket}`}
                          target="_blank" rel="noreferrer"
                          style={{ color: "#60a5fa", textDecoration: "none", fontWeight: 600 }}
                        >
                          #{a.id_ticket}
                        </a>
                      </td>
                      <td style={td}>
                        <span style={{ color, fontWeight: 600, fontSize: 12 }}>
                          {BUFFER_LABEL[a.buffer] ?? a.buffer}
                        </span>
                      </td>
                      <td style={td}>
                        <span style={{ color: ac, fontWeight: 700 }}>{formatDias(dias)}</span>
                        {critico && <span style={{ fontSize: 10, color: "#f87171", marginLeft: 6 }}>🔴 crítico</span>}
                        {acima   && <span style={{ fontSize: 10, color: "#fbbf24", marginLeft: 6 }}>⚠ acima da média</span>}
                      </td>
                      <td style={{ ...td, fontSize: 11, color: "#64748b" }}>
                        {a.entrada_estimada && (
                          <span title="Entrada estimada — ticket já estava neste buffer quando o rastreamento foi ativado" style={{ color: "#fbbf24", marginRight: 3 }}>~</span>
                        )}
                        {a.entrada_em ? new Date(a.entrada_em).toLocaleDateString("pt-BR") : "—"}
                      </td>
                      <td style={{ ...td, fontSize: 12, color: "#94a3b8" }}>{a.status_entrada || "—"}</td>
                      <td style={td}>
                        <span style={sprintChip}>{a.grupo_entrega || "—"}</span>
                      </td>
                      <td style={td}>
                        <button
                          style={{ ...finalizarBtn, opacity: finalizing === a.id_ticket ? 0.5 : 1 }}
                          disabled={finalizing === a.id_ticket}
                          onClick={() => handleFinalizar(a.id_ticket)}
                          title="Mover para Finalizados (controle interno, não altera o Redmine)"
                        >
                          {finalizing === a.id_ticket ? "..." : "→ Finalizar"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Histórico por ticket ── */}
      <section style={section}>
        <h2 style={sectionTitle}>Tempo acumulado por ticket</h2>
        <p style={sectionDesc}>
          Soma de todos os períodos em cada buffer. Clique em um ticket para ver o histórico detalhado de transições.
        </p>

        {ticketsSorted.length === 0 ? (
          <div style={emptyMsg}>Nenhum dado histórico disponível ainda.</div>
        ) : (
          <div style={tableWrap}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>Ticket</th>
                  <th style={{ ...th, color: BUFFER_COLOR.QA }}>QA</th>
                  <th style={{ ...th, color: BUFFER_COLOR.Producao }}>Produção</th>
                  <th style={{ ...th, color: BUFFER_COLOR["QA Testes"] }}>QA Testes</th>
                  <th style={{ ...th, color: BUFFER_COLOR.Entrega }}>Entrega</th>
                  <th style={th}>Total</th>
                  <th style={{ ...th, width: 24 }} />
                </tr>
              </thead>
              <tbody>
                {ticketsSorted.slice(0, 40).flatMap(t => {
                  const total    = BUFFER_ORDER.reduce((s, k) => s + (t.tempos?.[k] || 0), 0);
                  const isOpen   = expanded === t.id_ticket;
                  return [
                    <tr
                      key={t.id_ticket}
                      style={{ ...trRow, cursor: "pointer" }}
                      onClick={() => setExpanded(isOpen ? null : t.id_ticket)}
                    >
                      <td style={td}>
                        <a
                          href={`https://redmine.lexsis.com.br/issues/${t.id_ticket}`}
                          target="_blank" rel="noreferrer"
                          onClick={e => e.stopPropagation()}
                          style={{ color: "#60a5fa", textDecoration: "none", fontWeight: 600 }}
                        >
                          #{t.id_ticket}
                        </a>
                      </td>
                      <td style={{ ...td, color: BUFFER_COLOR.QA }}>{formatDias(t.tempos?.QA || null)}</td>
                      <td style={{ ...td, color: BUFFER_COLOR.Producao }}>{formatDias(t.tempos?.Producao || null)}</td>
                      <td style={{ ...td, color: BUFFER_COLOR["QA Testes"] }}>{formatDias(t.tempos?.["QA Testes"] || null)}</td>
                      <td style={{ ...td, color: BUFFER_COLOR.Entrega }}>{formatDias(t.tempos?.Entrega || null)}</td>
                      <td style={{ ...td, fontWeight: 700, color: "#e2e8f0" }}>{formatDias(total || null)}</td>
                      <td style={{ ...td, color: "#475569", textAlign: "center" }}>{isOpen ? "▲" : "▼"}</td>
                    </tr>,
                    isOpen && (
                      <tr key={`${t.id_ticket}-hist`}>
                        <td colSpan={7} style={{ padding: 0, border: "none" }}>
                          <div style={histPanel}>
                            <div style={{ fontSize: 11, color: "#475569", marginBottom: 10, fontWeight: 600, letterSpacing: "0.06em" }}>
                              HISTÓRICO DE TRANSIÇÕES — #{t.id_ticket}
                            </div>
                            {(t.historico || []).length === 0 ? (
                              <div style={{ color: "#475569", fontSize: 12 }}>Sem histórico registrado.</div>
                            ) : (
                              (t.historico).map((h, i) => {
                                const color = BUFFER_COLOR[h.buffer] ?? "#94a3b8";
                                return (
                                  <div key={i} style={histRow}>
                                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }} />
                                    <span style={{ color, fontWeight: 600, width: 86, fontSize: 12 }}>
                                      {BUFFER_LABEL[h.buffer] ?? h.buffer}
                                    </span>
                                    <span style={{ color: "#64748b", fontSize: 11, width: 120 }}>
                                      {h.entrada_estimada && (
                                        <span title="entrada estimada" style={{ color: "#fbbf24", marginRight: 2 }}>~</span>
                                      )}
                                      {h.entrada_em
                                        ? new Date(h.entrada_em).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
                                        : "—"}
                                    </span>
                                    <span style={{ color: "#334155", fontSize: 11, margin: "0 6px" }}>→</span>
                                    <span style={{ color: "#64748b", fontSize: 11, width: 120 }}>
                                      {h.saida_em
                                        ? new Date(h.saida_em).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
                                        : <span style={{ color: "#fbbf24" }}>aguardando</span>}
                                    </span>
                                    <span style={{ fontWeight: 700, color: "#e2e8f0", width: 72, fontSize: 12 }}>
                                      {formatDias(h.dias)}
                                    </span>
                                    {h.status_entrada && (
                                      <span style={statusChip}>{h.status_entrada}</span>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

    </div>
  );
}

/* ── Styles ───────────────────────────────────────── */

const page = {
  padding: "28px 32px",
  minHeight: "100vh",
  color: "#f1f5f9",
  fontFamily: "'Inter', sans-serif",
};

const header = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 28,
};

const h1 = {
  fontSize: 22,
  fontWeight: 800,
  color: "#f1f5f9",
  margin: 0,
  letterSpacing: "-0.01em",
};

const sprintBadge = {
  fontSize: 11,
  fontWeight: 600,
  color: "#60a5fa",
  background: "rgba(96,165,250,0.1)",
  border: "1px solid rgba(96,165,250,0.2)",
  borderRadius: 6,
  padding: "2px 10px",
};

const finalizarBtn = {
  padding: "4px 10px",
  borderRadius: 6,
  border: "1px solid rgba(74,222,128,0.25)",
  background: "rgba(74,222,128,0.06)",
  color: "#4ade80",
  fontSize: 10,
  fontWeight: 700,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const btnRefresh = {
  background: "rgba(96,165,250,0.08)",
  border: "1px solid rgba(96,165,250,0.15)",
  color: "#60a5fa",
  borderRadius: 8,
  padding: "6px 14px",
  cursor: "pointer",
  fontSize: 16,
};

const kpiRow = {
  display: "grid",
  gridTemplateColumns: "repeat(5, 1fr)",
  gap: 16,
  marginBottom: 32,
};

const kpiCard = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  border: "1px solid",
  borderRadius: 14,
  padding: "22px 24px",
};

const kpiRow2 = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 4,
};

const kpiLabel = {
  fontSize: 11,
  color: "#475569",
};

const kpiValue = {
  fontSize: 12,
  color: "#e2e8f0",
  fontWeight: 600,
};

const section = {
  marginBottom: 36,
};

const sectionTitle = {
  fontSize: 14,
  fontWeight: 700,
  color: "#e2e8f0",
  margin: "0 0 4px",
  letterSpacing: "0.02em",
};

const sectionDesc = {
  fontSize: 12,
  color: "#475569",
  margin: "0 0 14px",
  lineHeight: 1.5,
};

const emptyMsg = {
  color: "#334155",
  fontSize: 13,
  padding: "16px 0",
};

const tableWrap = {
  overflowX: "auto",
  borderRadius: 10,
  border: "1px solid rgba(96,165,250,0.07)",
};

const table = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};

const th = {
  textAlign: "left",
  padding: "10px 14px",
  fontSize: 11,
  fontWeight: 600,
  color: "#475569",
  background: "rgba(11,22,42,0.6)",
  borderBottom: "1px solid rgba(96,165,250,0.08)",
  whiteSpace: "nowrap",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
};

const td = {
  padding: "10px 14px",
  borderBottom: "1px solid rgba(255,255,255,0.04)",
  verticalAlign: "middle",
};

const trRow = {
  transition: "background 0.12s",
};

const sprintChip = {
  fontSize: 10,
  color: "#64748b",
  background: "rgba(71,85,105,0.2)",
  padding: "2px 7px",
  borderRadius: 4,
};

const histPanel = {
  background: "rgba(8,18,42,0.6)",
  borderTop: "1px solid rgba(255,255,255,0.04)",
  borderBottom: "1px solid rgba(255,255,255,0.04)",
  padding: "14px 20px 14px 32px",
};

const histRow = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "5px 0",
  borderBottom: "1px solid rgba(255,255,255,0.03)",
};

const statusChip = {
  fontSize: 10,
  color: "#64748b",
  background: "rgba(71,85,105,0.15)",
  padding: "2px 6px",
  borderRadius: 4,
  marginLeft: 4,
};
